import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import Login from './components/Login';
import SideNav from './components/SideNav';
import VehicleList from './components/VehicleList';
import TimelineView from './components/TimelineView';
import VideoDetails from './components/VideoDetails';
import AnnotationsView from './components/AnnotationsView';
import AnnotationTool from './components/AnnotationTool';
import { getStaticVehicles } from './services/staticData';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedMinuteData, setSelectedMinuteData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showAnnotationTool, setShowAnnotationTool] = useState(false);

  useEffect(() => {
    // Check if user is already authenticated
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleVehicleSelect = (vehicle) => {
    setSelectedVehicle(vehicle);
    // Navigation will be handled by React Router
  };

  const handleBackToVehicles = () => {
    setSelectedVehicle(null);
    setSelectedMinuteData(null);
    // Navigation will be handled by React Router
  };

  const handleBackToTimeline = () => {
    console.log('App - handleBackToTimeline called, returning to timeline with date:', selectedDate);
    setSelectedMinuteData(null);
    // Navigation will be handled by React Router
  };

  const handleMinuteClick = (minuteData) => {
    console.log('App - handleMinuteClick called with:', minuteData);
    setSelectedMinuteData(minuteData);
    // Navigation will be handled by React Router
  };

  const handleDateChange = (date) => {
    console.log('App - handleDateChange called with:', date);
    setSelectedDate(date);
  };

  const handleImageSelect = (image) => {
    setSelectedImage(image);
    setShowAnnotationTool(true);
  };

  const handleAnnotationSave = (annotationData) => {
    console.log('Annotation saved:', annotationData);
    setShowAnnotationTool(false);
    setSelectedImage(null);
  };

  const handleAnnotationToolClose = () => {
    setShowAnnotationTool(false);
    setSelectedImage(null);
  };

  // Wrapper component for TimelineView that handles URL parameters
  const TimelineWrapper = () => {
    const { vehicleId } = useParams();
    const [vehicle, setVehicle] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchVehicle = async () => {
        try {
          const vehicles = await getStaticVehicles();
          const foundVehicle = vehicles.find(v => v.id.toString() === vehicleId);
          setVehicle(foundVehicle);
        } catch (error) {
          console.error('Error fetching vehicle:', error);
        } finally {
          setLoading(false);
        }
      };

      if (vehicleId) {
        fetchVehicle();
      }
    }, [vehicleId]);

    if (loading) {
      return <div className="loading">Loading vehicle...</div>;
    }

    if (!vehicle) {
      return <Navigate to="/" replace />;
    }

    return (
      <TimelineView
        vehicle={vehicle}
        selectedDate={selectedDate}
        onBack={handleBackToVehicles}
        onMinuteClick={handleMinuteClick}
        onDateChange={handleDateChange}
      />
    );
  };

  // Wrapper component for VideoDetails that handles URL parameters
  const VideoDetailsWrapper = () => {
    const { vehicleId, minuteId } = useParams();
    const [vehicle, setVehicle] = useState(null);
    const [minuteData, setMinuteData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchData = async () => {
        try {
          const vehicles = await getStaticVehicles();
          const foundVehicle = vehicles.find(v => v.id.toString() === vehicleId);
          setVehicle(foundVehicle);

          if (foundVehicle && minuteId) {
            // Create a mock minute data object based on the URL parameter
            const minuteData = {
              minute: parseInt(minuteId),
              has_video: true
            };
            setMinuteData(minuteData);
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setLoading(false);
        }
      };

      if (vehicleId && minuteId) {
        fetchData();
      }
    }, [vehicleId, minuteId]);

    if (loading) {
      return <div className="loading">Loading video details...</div>;
    }

    if (!vehicle || !minuteData) {
      return <Navigate to="/" replace />;
    }

    return (
      <VideoDetails
        vehicle={vehicle}
        minuteData={minuteData}
        date={selectedDate}
        onBack={handleBackToTimeline}
      />
    );
  };

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <Router>
      <div className="App">
        <SideNav />
        
        <div className="main-content">
          <Routes>
            <Route path="/" element={<VehicleList onVehicleSelect={handleVehicleSelect} />} />
            <Route path="/annotations" element={<AnnotationsView onImageSelect={handleImageSelect} />} />
            <Route path="/timeline/:vehicleId" element={<TimelineWrapper />} />
            <Route path="/video/:vehicleId/:minuteId" element={<VideoDetailsWrapper />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>

        {showAnnotationTool && (
          <AnnotationTool
            image={selectedImage}
            onClose={handleAnnotationToolClose}
            onSave={handleAnnotationSave}
          />
        )}
      </div>
    </Router>
  );
}

export default App;
